import { AccordionPrimitive } from '@goaltree/ui/components/accordion'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { Link, useRouterState } from '@tanstack/react-router'
import { ChevronRightIcon } from 'lucide-react'

import { scrollActiveTabToTop } from '@/lib/scroll'

type NavSubItem =
  | { title: string; url: string; done?: boolean; separator?: never }
  | { separator: true; title: string; url?: never; done?: never }

const ACTIVE_ACCENT =
  'relative text-[13px] data-active:font-semibold data-active:before:absolute data-active:before:inset-y-2 data-active:before:left-0 data-active:before:w-[3px] data-active:before:rounded-r-full data-active:before:bg-primary group-data-[collapsible=icon]:before:hidden'

export function NavMain({
  label,
  items,
}: {
  label?: string
  items: {
    title: string
    url?: string
    onClick?: () => void
    icon?: React.ReactNode
    exact?: boolean
    count?: number
    badge?: number
    dot?: boolean
    dotLabel?: string
    meta?: string
    items?: NavSubItem[]
    // Always-open sub-items: the row keeps the background, the current sub-item the accent line.
    subnav?: React.ReactNode
  }[]
}) {
  const { location } = useRouterState()
  const currentPath = location.pathname.replace(/\/$/, '') || '/'

  return (
    <SidebarGroup className="px-3 py-1 group-data-[collapsible=icon]:px-2">
      {label && (
        <SidebarGroupLabel className="h-6 px-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
          {label}
        </SidebarGroupLabel>
      )}
      <SidebarMenu className="gap-1">
        {items.map((item) => {
          const base = item.url ? item.url.replace(/\/$/, '') || '/' : undefined
          const isActive =
            !!base &&
            (item.items
              ? currentPath === base ||
                item.items.some(
                  (sub) => !sub.separator && currentPath === (sub.url.replace(/\/$/, '') || '/'),
                )
              : currentPath === base ||
                (!item.exact &&
                  base !== '/' &&
                  currentPath.startsWith(base + '/') &&
                  // A more specific sibling (Clients vs Client plans) takes the highlight.
                  !items.some(
                    (o) =>
                      o.url &&
                      o.url.length > base.length &&
                      (currentPath === o.url || currentPath.startsWith(o.url + '/')),
                  )))

          if (!item.items?.length) {
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  tooltip={item.title}
                  isActive={isActive}
                  className={cn(
                    ACTIVE_ACCENT,
                    item.onClick && 'cursor-pointer',
                    item.subnav && 'data-active:before:hidden',
                  )}
                  onClick={item.onClick}
                  render={
                    item.url ? (
                      <Link
                        to={item.url}
                        onClick={() => {
                          // Tapping the route you're already on scrolls it to top.
                          if (currentPath === base) scrollActiveTabToTop()
                        }}
                      />
                    ) : undefined
                  }
                >
                  <span className={cn(isActive && 'text-primary-text')}>{item.icon}</span>
                  <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                  {(item.badge ?? 0) > 0 ? (
                    <span className="ml-auto flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold tabular-nums text-primary-text group-data-[collapsible=icon]:hidden">
                      {item.badge! > 99 ? '99+' : item.badge}
                    </span>
                  ) : item.dot ? (
                    <span
                      className="ml-auto h-1.5 w-1.5 rounded-full bg-primary group-data-[collapsible=icon]:hidden"
                      aria-label={item.dotLabel}
                    />
                  ) : item.meta ? (
                    <span className="ml-auto truncate text-[10px] font-medium tabular-nums text-muted-foreground/60 group-data-[collapsible=icon]:hidden">
                      {item.meta}
                    </span>
                  ) : (
                    (item.count ?? 0) > 0 && (
                      <span
                        className={cn(
                          'ml-auto text-[10px] font-medium tabular-nums group-data-[collapsible=icon]:hidden',
                          isActive
                            ? 'rounded-full bg-foreground/10 px-1.5 py-px font-semibold text-foreground'
                            : 'text-muted-foreground/60',
                        )}
                      >
                        {item.count}
                      </span>
                    )
                  )}
                </SidebarMenuButton>
                {item.subnav}
              </SidebarMenuItem>
            )
          }

          return (
            <SidebarMenuItem key={item.title}>
              <AccordionPrimitive.Root defaultValue={[item.title]} className="w-full">
                <AccordionPrimitive.Item value={item.title}>
                  <AccordionPrimitive.Header className="contents">
                    <SidebarMenuButton
                      tooltip={item.title}
                      isActive={isActive}
                      render={<Link to={item.url} />}
                      className={cn(
                        'group-hover/menu-item:bg-sidebar-accent group-hover/menu-item:text-sidebar-accent-foreground',
                        ACTIVE_ACCENT,
                      )}
                    >
                      <span className={cn(isActive && 'text-primary-text')}>{item.icon}</span>
                      <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                    </SidebarMenuButton>
                    <AccordionPrimitive.Trigger
                      render={<SidebarMenuAction />}
                      className="cursor-pointer group/acc-trigger"
                    >
                      <ChevronRightIcon className="transition-transform duration-200 group-aria-expanded/acc-trigger:rotate-90" />
                    </AccordionPrimitive.Trigger>
                  </AccordionPrimitive.Header>
                  <AccordionPrimitive.Panel className="overflow-hidden data-open:animate-accordion-down data-closed:animate-accordion-up">
                    <div className="h-(--accordion-panel-height) data-ending-style:h-0 data-starting-style:h-0">
                      <SidebarMenuSub className="mr-0 pr-0 mt-1.5 gap-1">
                        {item.items.map((sub, i) =>
                          sub.separator ? (
                            <SidebarMenuSubItem key={`sep-${i}`}>
                              <span className="block px-2.5 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/30">
                                {sub.title}
                              </span>
                            </SidebarMenuSubItem>
                          ) : (
                            <SidebarMenuSubItem key={sub.title}>
                              <SidebarMenuSubButton
                                isActive={currentPath === (sub.url.replace(/\/$/, '') || '/')}
                                render={<Link to={sub.url} title={sub.title} />}
                                className="h-8!"
                              >
                                <span
                                  className={`truncate${sub.done ? ' line-through opacity-50' : ''}`}
                                >
                                  {sub.title}
                                </span>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ),
                        )}
                      </SidebarMenuSub>
                    </div>
                  </AccordionPrimitive.Panel>
                </AccordionPrimitive.Item>
              </AccordionPrimitive.Root>
            </SidebarMenuItem>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
